import axios from 'axios';
import i18n from 'i18next';

export interface ApiErrorPayload {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
  upgradeUrl?: string;
  /** The cheapest plan sold that allows it (ADR-007: never Classic). */
  requiredPlan?: 'pro' | 'agency';
  currentPlan?: string;
  requestId?: string;
}

/**
 * Extracts the structured `{ code, message, ... }` payload our API wraps under
 * `{ success: false, error: { ... } }`. Returns null if the error doesn't
 * match this shape (network errors, non-axios errors, etc).
 */
export function getApiErrorPayload(err: unknown): ApiErrorPayload | null {
  if (!axios.isAxiosError(err)) return null;
  const body = err.response?.data;
  if (!body || typeof body !== 'object') return null;
  if ('error' in body && body.error && typeof body.error === 'object') {
    return body.error as ApiErrorPayload;
  }
  // Some endpoints surface flat shapes — accept both.
  if ('code' in body || 'message' in body) {
    return body as ApiErrorPayload;
  }
  return null;
}

/**
 * The sold plan a plan refusal names (`error.details.requiredPlan`, or a flat
 * `requiredPlan`), or null. Classic is not sold: a refusal naming it offers Pro.
 */
export function requiredPlanOf(err: unknown): 'pro' | 'agency' | null {
  const payload = getApiErrorPayload(err);
  const raw = payload?.details?.['requiredPlan'] ?? payload?.requiredPlan;
  if (typeof raw !== 'string') return null;
  const id = raw.toLowerCase();
  if (id === 'agency') return 'agency';
  return id === 'pro' || id === 'classic' ? 'pro' : null;
}

export function isPlanRequiredError(err: unknown): boolean {
  return getApiErrorPayload(err)?.code === 'PLAN_REQUIRED';
}

/** The error code, when the API sent one. */
export function getApiErrorCode(err: unknown): string | null {
  return getApiErrorPayload(err)?.code ?? null;
}

/** `error.details`, or an empty object. */
export function getApiErrorDetails(err: unknown): Record<string, unknown> {
  const details = getApiErrorPayload(err)?.details;
  return details && typeof details === 'object' ? details : {};
}

/** A number from `error.details`, or null. */
export function detailNumber(details: Record<string, unknown>, key: string): number | null {
  const v = details[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

export function isSeatLimitError(err: unknown): boolean {
  const payload = getApiErrorPayload(err);
  if (!payload) return false;
  // TeamSeatGuard throws TEAM_SEAT_LIMIT_REACHED; its message ("allows N team
  // members") never matched the regex, so the seat error went unrecognised.
  if (payload.code === 'TEAM_SEAT_LIMIT_REACHED' || payload.code === 'SEAT_LIMIT_REACHED') return true;
  return /seat limit/i.test(payload.message ?? '');
}

/**
 * Error codes the app has its own words for. The server's message (English)
 * is never shown for these: the reader sees the translation.
 */
export const ERROR_MESSAGE_KEYS: Readonly<Record<string, string>> = {
  // API keys (AL-41).
  API_KEY_LIMIT_REACHED: 'apiKeys.errors.limitReached',
  API_KEY_EXPIRY_PAST: 'apiKeys.errors.expiryPast',
  // Team and invitations (AL-38): every refusal in the reader's language.
  ALREADY_MEMBER: 'errors.team.ALREADY_MEMBER',
  INVITE_ALREADY_PENDING: 'errors.team.INVITE_ALREADY_PENDING',
  INVITE_DAILY_LIMIT: 'errors.team.INVITE_DAILY_LIMIT',
  CANNOT_INVITE_SELF: 'errors.team.CANNOT_INVITE_SELF',
  INVITE_RESEND_COOLDOWN: 'errors.team.INVITE_RESEND_COOLDOWN',
  INVITE_NOT_FOUND: 'errors.team.INVITE_NOT_FOUND',
  INVITE_NOT_PENDING: 'errors.team.INVITE_NOT_PENDING',
  INVITE_EXPIRED: 'errors.team.INVITE_EXPIRED',
  INVITE_REVOKED: 'errors.team.INVITE_REVOKED',
  INVITE_DECLINED: 'errors.team.INVITE_DECLINED',
  INVITE_ACCEPTED: 'errors.team.INVITE_ACCEPTED',
  INVITE_EMAIL_MISMATCH: 'errors.team.INVITE_EMAIL_MISMATCH',
  EMAIL_NOT_CONFIRMED: 'errors.team.EMAIL_NOT_CONFIRMED',
  INVITE_LINK_REQUIRED: 'errors.team.INVITE_LINK_REQUIRED',
  INVITE_ACCOUNT_BLOCKED: 'errors.team.INVITE_ACCOUNT_BLOCKED',
  INVITES_UNAVAILABLE: 'errors.team.INVITES_UNAVAILABLE',
  MANAGERS_ONLY: 'errors.team.MANAGERS_ONLY',
  ADMIN_GRANT_OWNER_ONLY: 'errors.team.ADMIN_GRANT_OWNER_ONLY',
  BILLING_GRANT_ACCOUNT_OWNER_ONLY: 'errors.team.BILLING_GRANT_ACCOUNT_OWNER_ONLY',
  OWN_ROLE: 'errors.team.OWN_ROLE',
  OWNER_ROLE_FIXED: 'errors.team.OWNER_ROLE_FIXED',
  ADMIN_CHANGE_OWNER_ONLY: 'errors.team.ADMIN_CHANGE_OWNER_ONLY',
  OWNER_NOT_REMOVABLE: 'errors.team.OWNER_NOT_REMOVABLE',
  ADMIN_REMOVE_OWNER_ONLY: 'errors.team.ADMIN_REMOVE_OWNER_ONLY',
  MEMBER_NOT_FOUND: 'errors.team.MEMBER_NOT_FOUND',
  OWNER_CANNOT_LEAVE: 'errors.team.OWNER_CANNOT_LEAVE',
  RECIPIENT_ROLE_NOT_ALLOWED: 'errors.team.RECIPIENT_ROLE_NOT_ALLOWED',
  TRANSFER_TO_SELF: 'errors.team.TRANSFER_TO_SELF',
  TRANSFER_NOT_FOUND: 'errors.team.TRANSFER_NOT_FOUND',
  TRANSFER_EXPIRED: 'errors.team.TRANSFER_EXPIRED',
  OWNER_ONLY: 'errors.team.OWNER_ONLY',
  // Two-step verification (apps/api common/auth/mfa.ts).
  MFA_REQUIRED: 'settings.reauth.mfaNeeded',
  MFA_ENROLMENT_REQUIRED: 'auth.staffMfa.body',
  MFA_CHECK_UNAVAILABLE: 'auth.errors.generic',
  // A revoked session (signed out elsewhere, password changed, banned, deleted).
  SESSION_REVOKED: 'auth.errors.sessionExpired',
  // DELETE /account and other account-ending changes need a sign-in in the last 10 minutes.
  REAUTH_REQUIRED: 'settings.reauth.signInAgainNeeded',
  REAUTH_CHECK_UNAVAILABLE: 'auth.errors.generic',
  // Settings → Security (apps/api modules/auth/auth-security.service.ts).
  RECOVERY_CODE_INVALID: 'auth.mfa.recoveryWrong',
  MFA_NOT_ENABLED: 'settings.securityTab.mfaNotEnabled',
  SESSION_IS_CURRENT: 'settings.securityTab.revokeCurrent',
  SESSION_NOT_FOUND: 'settings.securityTab.sessionGone',
  ALREADY_SUBSCRIBED: 'errors.alreadySubscribed',
  INTERVAL_UNAVAILABLE: 'errors.intervalUnavailable',
  PLAN_NOT_SOLD: 'errors.planNotSold',
  NOTHING_TO_CANCEL: 'errors.nothingToCancel',
  TOO_MANY_BRANDS: 'errors.tooManyBrands',
  TARGET_AT_BRAND_LIMIT: 'errors.targetAtBrandLimit',
  EXTRA_BRAND_REQUIRED: 'errors.extraBrandRequired',
  EXTRA_BRAND_AUTHORIZATION_REQUIRED: 'errors.extraBrandAuthorizationRequired',
  EXTRA_BRAND_ADMIN_MANAGED: 'topbar.addBrandSheet.block.paidAgency.body',
  EXTRA_BRAND_REQUIRES_PAID_AGENCY: 'topbar.addBrandSheet.block.paidAgency.body',
  EXTRA_BRAND_NOT_ON_PLAN: 'topbar.addBrandSheet.block.paidAgency.body',
  EXTRA_BRAND_BILLING_UNAVAILABLE: 'account.extraBrandBillingUnavailable',
  EXTRA_BRAND_BILLING_FAILED: 'errors.extraBrandBillingFailed',
  RAZORPAY_PLAN_PRICE_MISMATCH: 'errors.razorpayPlanPriceMismatch',
  // A Razorpay or Paddle call failed: nothing was charged (502).
  PAYMENT_PROVIDER_ERROR: 'errors.paymentProviderError',
  // Razorpay has not confirmed the subscription yet; its webhook applies it.
  PAYMENT_NOT_CONFIRMED: 'errors.paymentNotConfirmed',
  ACCOUNT_OWNER_REQUIRED: 'errors.accountOwnerRequired',
  PLAN_REQUIRED: 'toast.planRequired',
  LIMIT_REACHED: 'toast.planRequired',
  // X's monthly post credits are spent (a post 1, a post with a link 13).
  X_POST_LIMIT_REACHED: 'errors.xPostLimitReached',
  POST_PUBLISHING: 'errors.postPublishing',
  // Changed at the same time elsewhere (another tab, a teammate, an approval link).
  POST_CHANGED: 'errors.postChanged',
  // The publish queue could not be reached; the post went back as it was.
  PUBLISH_QUEUE_UNAVAILABLE: 'errors.publishQueueUnavailable',
  SCHEDULE_TIME_PAST: 'errors.scheduleTimePast',
  SCHEDULE_TIME_REQUIRED: 'errors.scheduleTimeRequired',
  TEAM_SEAT_LIMIT_REACHED: 'settings.teamTab.seatLimit',
  LAST_BRAND: 'settings.brandTab.lastBrand',
  CONFIRM_NAME_MISMATCH: 'settings.brandTab.nameMismatch',
  WORKSPACE_MISMATCH: 'errors.workspaceMismatch',
  // A channel an admin has not opened yet ("coming soon").
  CHANNEL_UNAVAILABLE: 'channels.comingSoonHint',
  // A WhatsApp number can be connected to one brand only.
  WHATSAPP_NUMBER_IN_USE: 'errors.whatsappNumberInUse',
  // An Instagram mention whose text Meta did not give: nothing to draft from.
  MESSAGE_UNREADABLE: 'errors.messageUnreadable',
  // A WhatsApp marketing opt-in recorded without where it was given.
  OPT_IN_SOURCE_REQUIRED: 'errors.optInSourceRequired',
  // Thrown by the Razorpay and Paddle script loaders (shared/lib).
  PAYMENTS_UNAVAILABLE: 'errors.paymentsUnavailable',
  // Inbox replies, WhatsApp templates and the inbox agent.
  REPLY_WINDOW_CLOSED: 'errors.replyWindowClosed',
  X_NOT_REPLYABLE: 'errors.xNotReplyable',
  REPLY_NOT_SUPPORTED: 'errors.replyNotSupported',
  TEMPLATE_NOT_FOUND: 'errors.templateNotFound',
  TEMPLATE_UNSUPPORTED: 'errors.templateUnsupported',
  TEMPLATE_MEDIA_INVALID: 'errors.templateMediaInvalid',
  TEMPLATE_VALUES_INVALID: 'errors.templateValuesInvalid',
  CUSTOMER_OPTED_OUT: 'errors.customerOptedOut',
  MARKETING_OPT_IN_REQUIRED: 'errors.marketingOptInRequired',
  CUSTOMER_BLOCKED: 'errors.customerBlocked',
  // The brand switched off sending customer messages to the AI.
  AI_PROCESSING_OFF: 'errors.aiProcessingOff',
  AGENT_NOT_AVAILABLE_ON_CHANNEL: 'errors.agentNotAvailableOnChannel',
  AI_UNAVAILABLE: 'errors.aiUnavailable',
  AGENT_CONSENT_REQUIRED: 'errors.agentConsentRequired',
  AGENT_NOT_ON_AUTO: 'errors.agentNotOnAuto',
  // Undo came after the automatic reply had already gone out.
  REPLY_ALREADY_SENT: 'errors.replyAlreadySent',
  // Longer than the platform takes for this kind of reply.
  REPLY_TOO_LONG: 'inbox.composer.tooLong',
  // Channel connections (only codes whose words need no {{platform}}).
  OAUTH_STATE_EXPIRED: 'errors.oauthStateExpired',
  OAUTH_INCOMPLETE: 'errors.oauthIncomplete',
  OAUTH_PLATFORM_MISMATCH: 'errors.oauthPlatformMismatch',
  GBP_ACCESS_DENIED: 'errors.gbpAccessDenied',
  GBP_LOCATIONS_UNAVAILABLE: 'errors.gbpLocationsUnavailable',
  GBP_NO_LOCATION: 'errors.gbpNoLocation',
  META_PAGES_UNAVAILABLE: 'errors.metaPagesUnavailable',
  META_NO_PAGE: 'errors.metaNoPage',
  META_NO_INSTAGRAM_PAGE: 'errors.metaNoInstagramPage',
  TELEGRAM_TOKEN_REJECTED: 'errors.telegramTokenRejected',
  TELEGRAM_CHAT_ID_REQUIRED: 'errors.telegramChatIdRequired',
  TELEGRAM_CHAT_NOT_FOUND: 'errors.telegramChatNotFound',
  TELEGRAM_BOT_NOT_IN_CHAT: 'errors.telegramBotNotInChat',
  TELEGRAM_WEBHOOK_REJECTED: 'errors.telegramWebhookRejected',
  TELEGRAM_UNREACHABLE: 'errors.telegramUnreachable',
  WHATSAPP_SIGNUP_REJECTED: 'errors.whatsappSignupRejected',
  WHATSAPP_TOKEN_REJECTED: 'errors.whatsappTokenRejected',
  WHATSAPP_WABA_NOT_ACCESSIBLE: 'errors.whatsappWabaNotAccessible',
  WHATSAPP_PHONE_NUMBERS_UNREADABLE: 'errors.whatsappPhoneNumbersUnreadable',
  WHATSAPP_PHONE_NOT_FOUND: 'errors.whatsappPhoneNotFound',
  WHATSAPP_SUBSCRIBE_FAILED: 'errors.whatsappSubscribeFailed',
  WHATSAPP_REGISTER_FAILED: 'errors.whatsappRegisterFailed',
  PINTEREST_BOARD_NOT_FOUND: 'errors.pinterestBoardNotFound',
  ZETPIN_HANDLE_REQUIRED: 'errors.zetpinHandleRequired',
  ZETPIN_TOKEN_REJECTED: 'errors.zetpinTokenRejected',
  ZETPIN_ACCOUNT_NOT_FOUND: 'errors.zetpinAccountNotFound',
  ZETPIN_ACCOUNT_MISMATCH: 'errors.zetpinAccountMismatch',
  ZETPIN_UNREACHABLE: 'errors.zetpinUnreachable',
  // Posts: retry and approval.
  POST_NOT_RETRYABLE: 'errors.postNotRetryable',
  NOTHING_TO_RETRY: 'errors.nothingToRetry',
  RETRY_MAY_DOUBLE_POST: 'errors.retryMayDoublePost',
  APPROVAL_REQUIRED: 'errors.approvalRequired',
  // A media file one of the selected channels cannot take.
  MEDIA_NOT_ALLOWED: 'errors.mediaNotAllowed',
  // Posts: said in the reader's language, never the API's English (F20).
  CHANNEL_NOT_READY: 'errors.channelNotReady',
  MEDIA_PROCESSING: 'errors.mediaProcessing',
  MEDIA_FAILED: 'errors.mediaFailed',
  MEDIA_NOT_FOUND: 'errors.mediaNotFound',
  PLATFORM_REQUIRED: 'errors.platformRequired',
  PUBLISHING_PAUSED: 'errors.publishingPaused',
  EDIT_TOO_LONG: 'errors.editTooLong',
  POST_STATUS_NOT_ALLOWED: 'errors.postStatusNotAllowed',
  POST_NOT_EDITABLE: 'errors.postNotEditable',
  POST_NOT_SUBMITTABLE: 'errors.postNotSubmittable',
  POST_NOT_PENDING: 'errors.postNotPending',
  POST_NOT_PUBLISHABLE: 'errors.postNotPublishable',
  NOT_APPROVER: 'composer.approvals.notApprover',
  // Posts → Import (CSV import of posts, F01).
  IMPORT_FILE_REQUIRED: 'postImport.errors.fileRequired',
  IMPORT_FILE_TOO_LARGE: 'postImport.errors.fileTooLarge',
  IMPORT_NOT_CSV: 'postImport.errors.notCsv',
  IMPORT_NOT_UTF8: 'postImport.errors.notUtf8',
  IMPORT_MALFORMED_CSV: 'postImport.errors.malformed',
  IMPORT_MISSING_COLUMNS: 'postImport.errors.missingColumns',
  IMPORT_NO_ROWS: 'postImport.errors.noRows',
  IMPORT_TOO_MANY_ROWS: 'postImport.errors.tooManyRows',
  IMPORT_NOTHING_TO_IMPORT: 'postImport.errors.nothingToImport',
  IMPORT_IN_PROGRESS: 'postImport.errors.inProgress',
  IMPORT_QUEUE_UNAVAILABLE: 'postImport.errors.queueUnavailable',
  IMPORT_NOT_FOUND: 'postImport.errors.notFound',
  // Media: a file still used by unpublished posts, or one deleted from the library.
  MEDIA_IN_USE: 'errors.mediaInUse',
  MEDIA_LEAVES_POST_EMPTY: 'errors.mediaLeavesPostEmpty',
  MEDIA_DELETED: 'errors.mediaDeleted',
  // WorkspaceGuard: the caller's role on the brand may not do this.
  ROLE_REQUIRED: 'errors.roleRequired',
  // D-01: what the plan paused, and the owner's choice of what stays active.
  MEMBER_PAUSED: 'errors.memberPaused',
  BRAND_PAUSED: 'errors.brandPaused',
  PLAN_LIMIT_CHOICE_TOO_MANY: 'planLimits.sheet.tooMany',
  // Billing (AL-05, M-03, M-11).
  ADMIN_MANAGED: 'errors.adminManaged',
  PAYMENT_METHOD_UNAVAILABLE: 'settings.billingTab.paymentMethodFailed',
  PLAN_PURCHASABLE: 'errors.planPurchasable',
  // Ownership can pass only to someone who has opened the brand (accepted).
  RECIPIENT_NOT_ACTIVE: 'settings.teamTab.transferRecipientNotActive',
  // POST /auth/me/avatar/presign.
  AVATAR_TYPE_UNSUPPORTED: 'settings.profileTab.notImage',
  AVATAR_TOO_LARGE: 'settings.profileTab.tooLarge',
  // Planning and creative (calendar, posters, media library).
  // A post an approver sent back: it is sent for approval again first (PC-02).
  RESUBMISSION_REQUIRED: 'errors.resubmissionRequired',
  // The browser stopped waiting for a poster; it lands in the media library (PC-07).
  POSTER_STILL_WORKING: 'occasions.generate.stillWorking',
  // Regenerate could not make a new design: nothing was used (PC-18).
  POSTER_NO_NEW_DESIGN: 'occasions.generate.noNewDesign',
  MEDIA_TYPE_UNSUPPORTED: 'errors.mediaTypeUnsupported',
  // The plan could not be handed to its queue; the plan is failed for a retry (PC-08).
  PLAN_QUEUE_UNAVAILABLE: 'plan.failure.UNKNOWN',
  // A plan item whose time has passed: a new time first (PC-28).
  PLAN_ITEM_PAST: 'plan.item.timePassed',
  PLAN_ITEM_ALREADY_OUT: 'plan.errors.alreadyOut',

  // GST invoices and the Indian billing identity (AL-02, AL-03, AL-17).
  GSTIN_INVALID: 'settings.billingDetails.gstinErrors.checksum',
  GSTIN_STATE_MISMATCH: 'settings.billingDetails.gstinErrors.stateMismatch',
  BILLING_STATE_INVALID: 'settings.billingDetails.stateInvalid',
  INVOICES_UNAVAILABLE: 'settings.invoices.loadFailed',
  INVOICE_PDF_PENDING: 'settings.invoices.pdfPending',
  INVOICE_NOT_ISSUED: 'settings.invoices.notIssued',
  INVOICE_NOT_FOUND: 'settings.invoices.notFound',

  // The background data export (AL-19, M-07) and the brand profile's version check (AL-42).
  EXPORT_LIMIT_REACHED: 'settings.privacy.exportLimit',
  EXPORT_EXPIRED: 'settings.privacy.exportExpiredNote',
  EXPORT_NOT_READY: 'settings.privacy.exportNotReady',
  EXPORT_NOT_FOUND: 'settings.privacy.exportNotReady',
  PROFILE_CONFLICT: 'brand.saveConflict',

  // Supabase Auth error codes (AuthError.code). Its messages are English
  // sentences; these are what the reader sees instead.
  invalid_credentials: 'auth.errors.invalidCredentials',
  email_not_confirmed: 'auth.messages.verifyFirst',
  over_request_rate_limit: 'auth.errors.rateLimited',
  over_email_send_rate_limit: 'auth.errors.rateLimited',
  over_sms_send_rate_limit: 'auth.errors.rateLimited',
  weak_password: 'auth.errors.weakPassword',
  user_already_exists: 'auth.messages.alreadyExists',
  email_exists: 'auth.messages.alreadyExists',
  same_password: 'auth.errors.samePassword',
  email_address_invalid: 'auth.errors.emailInvalid',
  otp_expired: 'auth.messages.linkExpired',
  access_denied: 'auth.errors.signInCancelled',
  session_not_found: 'auth.errors.sessionExpired',
  session_expired: 'auth.errors.sessionExpired',
  mfa_verification_failed: 'auth.errors.mfaCodeWrong',
  mfa_challenge_expired: 'auth.errors.mfaChallengeExpired',
  mfa_factor_not_found: 'auth.errors.mfaFactorMissing',
  insufficient_aal: 'auth.errors.insufficientAal',
  user_banned: 'auth.errors.userBanned',
  signup_disabled: 'auth.errors.signupDisabled',
  email_address_not_authorized: 'auth.errors.emailNotAuthorized',
  flow_state_expired: 'auth.messages.linkExpired',
  flow_state_not_found: 'auth.messages.linkExpired',
};

/** "Instagram: takes photos from 4:5 …" for the first of `error.details.issues`, or null. */
function firstIssueText(err: unknown): string | null {
  const issues = getApiErrorDetails(err)['issues'];
  if (!Array.isArray(issues) || issues.length === 0) return null;
  const first = issues[0] as { messageKey?: unknown; params?: unknown; label?: unknown; platform?: unknown };
  if (typeof first.messageKey !== 'string' || !i18n.exists(first.messageKey)) return null;
  const params = (first.params && typeof first.params === 'object' ? first.params : {}) as Record<string, unknown>;
  const label = typeof first.label === 'string' ? first.label : typeof params['platform'] === 'string' ? params['platform'] : null;
  const text = i18n.t(first.messageKey, params);
  // Most issue sentences name their channel already; only those that do not get it in front.
  return label && !text.includes(label) ? `${label}: ${text}` : text;
}

/** A Supabase AuthError (auth-js marks each one with `__isAuthError`). */
function isSupabaseAuthError(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { __isAuthError?: unknown }).__isAuthError === true;
}

/** The error code of an upload or poster that would not fit the account's storage (PC-21). */
export const STORAGE_LIMIT_REACHED = 'STORAGE_LIMIT_REACHED';

/** 500 MB, 5 GB: a storage size in the reader's units and digits. */
export function formatStorageSize(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  const locale = i18n.language || 'en';
  try {
    return gb >= 1
      ? new Intl.NumberFormat(locale, { style: 'unit', unit: 'gigabyte', maximumFractionDigits: 1 }).format(gb)
      : new Intl.NumberFormat(locale, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 0 }).format(bytes / 1024 ** 2);
  } catch {
    return gb >= 1 ? `${gb.toFixed(1)} GB` : `${Math.round(bytes / 1024 ** 2)} MB`;
  }
}

/** "Storage is full (500 MB). Delete media, or move to Pro for more room." */
export function storageFullMessage(limitBytes: unknown, plan: 'pro' | 'agency' | null): string {
  const limit = typeof limitBytes === 'number' && Number.isFinite(limitBytes) && limitBytes > 0 ? formatStorageSize(limitBytes) : null;
  if (!limit) return i18n.t('errors.storageFullPlain');
  if (plan) return i18n.t('errors.storageFullUpgrade', { limit, plan: i18n.t(`overview.plans.${plan}`) });
  return i18n.t('errors.storageFull', { limit });
}

/** Codes the API's exception filter gives any HttpException without its own: no meaning, English text. */
const GENERIC_CODE = /^(HTTP_\d{3}|INTERNAL_ERROR|VALIDATION_ERROR)$/;

/** The error's code: the API's, or one an app loader set on its Error. */
export function errorCodeOf(err: unknown): string | null {
  const fromApi = getApiErrorPayload(err)?.code;
  if (typeof fromApi === 'string') return fromApi;
  const own = (err as { code?: unknown } | null)?.code;
  return !axios.isAxiosError(err) && typeof own === 'string' ? own : null;
}

/**
 * A message for an error, in the reader's language wherever the app knows
 * the code (ERROR_MESSAGE_KEYS). The server's text is used only as the last
 * resort for a specific code the app does not know yet; generic codes,
 * network failures and loader errors show `fallback`. An Error the app
 * threw itself (already translated) keeps its message.
 */
export function getApiErrorMessage(err: unknown, fallback = 'Something went wrong'): string {
  const code = errorCodeOf(err);
  // A plan refusal names the plan that opens it when the API said which:
  // never only "a higher plan".
  // Storage full: the limit, and the plan that has more room (PC-21).
  if (code === STORAGE_LIMIT_REACHED) return storageFullMessage(getApiErrorDetails(err).limitBytes, requiredPlanOf(err));
  if (code === 'MEDIA_TOO_LARGE') {
    const max = detailNumber(getApiErrorDetails(err), 'maxBytes');
    if (max !== null) return i18n.t('errors.mediaTooLarge', { max: formatStorageSize(max) });
  }
  if (code === 'PLAN_REQUIRED' || code === 'LIMIT_REACHED') {
    const plan = requiredPlanOf(err);
    if (plan && code === 'PLAN_REQUIRED') return i18n.t('toast.planRequiredNamed', { plan: i18n.t(`overview.plans.${plan}`) });
  }
  // A refusal that lists the composer's own issues (MEDIA_NOT_ALLOWED,
  // CHANNEL_NOT_READY): the first one, worded as the composer words it.
  const issue = firstIssueText(err);
  if (issue) return issue;
  const key = code ? ERROR_MESSAGE_KEYS[code] : undefined;
  if (key && i18n.exists(key)) return i18n.t(key);
  if (code) {
    const message = getApiErrorPayload(err)?.message;
    return message && !GENERIC_CODE.test(code) ? message : fallback;
  }
  if (axios.isAxiosError(err)) return fallback;
  // Supabase's own sentences are English: never shown.
  if (isSupabaseAuthError(err)) return fallback;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/**
 * Like getApiErrorMessage, but only an error that carries a code (the API's,
 * a loader's or Supabase's) is put into words; anything else (an S3 upload
 * failure, a plain Error from a service) shows `fallback`, never its English.
 */
export function getCodedErrorMessage(err: unknown, fallback: string): string {
  return errorCodeOf(err) ? getApiErrorMessage(err, fallback) : fallback;
}
